import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Link, useSearchParams } from 'react-router-dom';
import { getTests } from '../../utils/api';
import DOMPurify from 'dompurify';
import ShortNews from '../shorts/News';
import Article from '../tests/article';
import { Helmet } from 'react-helmet-async';
import AdSlot from '../ads/AdSlot';

import '../../assets/css/tests.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const ListView = ({ tests }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialPage = Number(searchParams.get('page')) > 0 ? Number(searchParams.get('page')) : 1;
    const initialPageSizeParam = Number(searchParams.get('pageSize'));
    const initialPageSize = PAGE_SIZE_OPTIONS.includes(initialPageSizeParam)
        ? initialPageSizeParam
        : PAGE_SIZE_OPTIONS[0];

    const [currentPage, setCurrentPage] = useState(initialPage);
    const [pageSize, setPageSize] = useState(initialPageSize);

    // Bei Datenwechsel Seite aus URL übernehmen
    useEffect(() => {
        const fromUrl = Number(searchParams.get('page'));
        const psFromUrl = Number(searchParams.get('pageSize'));
        setCurrentPage(fromUrl > 0 ? fromUrl : 1);
        if (PAGE_SIZE_OPTIONS.includes(psFromUrl)) setPageSize(psFromUrl);
    }, [tests.length]);

    // URL sync + nach oben scrollen
    useEffect(() => {
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            if (currentPage > 1) p.set('page', String(currentPage)); else p.delete('page');
            if (pageSize !== PAGE_SIZE_OPTIONS[0]) p.set('pageSize', String(pageSize)); else p.delete('pageSize');
            return p;
        }, { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage, pageSize, setSearchParams]);

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(tests.length / pageSize)),
        [tests.length, pageSize]
    );

    const paginatedTests = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return tests.slice(start, start + pageSize);
    }, [tests, currentPage, pageSize]);

    const goToPage = (page) => {
        const p = Math.min(totalPages, Math.max(1, page));
        setCurrentPage(p);
    };

    const handlePageSizeChange = (e) => {
        const next = Number(e.target.value);
        if (!PAGE_SIZE_OPTIONS.includes(next)) return;
        setCurrentPage(1);
        setPageSize(next);
    };

    const truncateText = (text, maxLength = 240) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        const truncated = text.slice(0, maxLength);
        return truncated.slice(0, truncated.lastIndexOf(' ')) + '...';
    };

    // Seitenliste mit Ellipsen
    const getPageItems = (total, current) => {
        const items = [];
        if (total <= 7) { for (let i = 1; i <= total; i++) items.push(i); return items; }
        const add = (v) => items.push(v);
        add(1);
        const left = Math.max(2, current - 1);
        const right = Math.min(total - 1, current + 1);
        if (left > 2) add('…');
        for (let i = left; i <= right; i++) add(i);
        if (right < total - 1) add('…');
        add(total);
        return items;
    };

    const pageTitle = `Tests & Reviews – Seite ${currentPage}`;
    const pageDescription = `Aktuelle Tests & Reviews – Seite ${currentPage}. Spiele-Reviews, Eindrücke und Wertungen auf NGCTV.`;

    return (
        <>
            {/* SEO / Head */}
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <link rel="canonical" href={`https://ngctv.de/tests`} />
            </Helmet>

            <main>
                <div className='wrapper'>
                    <div className='rows'>
                        <div className='row'>
                            <div className='category'>Tests & Reviews</div>

                            <div className='tests-controls'>
                                <label htmlFor='pageSize' className='page-size-label'>Artikel pro Seite:&nbsp;</label>
                                <select id='pageSize' value={pageSize} onChange={handlePageSizeChange} className='page-size-select' aria-label='Artikel pro Seite wählen'>
                                    {PAGE_SIZE_OPTIONS.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>

                            <div className='tests'>
                                {paginatedTests.length === 0 ? (
                                    <div className='tests-empty'>Keine Einträge vorhanden.</div>
                                ) : (
                                    paginatedTests.map((test) => (
                                        <Link to={`/tests/${test.ID}`} viewTransition key={test.ID}>
                                            <div className='test'>
                                                <img src={`/tests/img/games/${test.cover}`} alt={test.titel} />
                                                <div>
                                                    <div className='titel'>{test.titel}</div>
                                                    <div className='author'>{test.author}</div>
                                                    <div className='content'><p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(truncateText(test.infotext)) }} /></div>
                                                    <div className='test-info'>
                                                        <div>Genre: {test.genre}</div>
                                                        <div>Plattform: {test.plattform}</div>
                                                        <div>Erscheinungstermin: {test.erscheinung}</div>
                                                        <div>Kosten: {test.kosten}</div>
                                                        <div>Entwickler: {test.entwickler}</div>
                                                        <div>Publisher: {test.publisher}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    ))
                                )}
                            </div>

                            {totalPages > 1 && (
                                <nav className='pagination' aria-label='Tests Pagination'>
                                    <button
                                        type='button'
                                        className='page-btn prev'
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        aria-label='Vorherige Seite'
                                    >
                                        « Zurück
                                    </button>

                                    <ul className='page-list' role='list'>
                                        {getPageItems(totalPages, currentPage).map((item, idx) => (
                                            <li key={idx}>
                                                {item === '…' ? (
                                                    <span className='page-ellipsis' aria-hidden>…</span>
                                                ) : (
                                                    <button
                                                        type='button'
                                                        className={`page-btn number ${item === currentPage ? 'active' : ''}`}
                                                        onClick={() => goToPage(item)}
                                                        aria-current={item === currentPage ? 'page' : undefined}
                                                        aria-label={`Seite ${item}`}
                                                    >
                                                        {item}
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        type='button'
                                        className='page-btn next'
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        aria-label='Nächste Seite'
                                    >
                                        Weiter »
                                    </button>
                                </nav>
                            )}
                        </div>

                        <div className='row'>
                            <ShortNews />
                            <AdSlot slot="2119524913" />
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

const Tests = () => {
    const [testsData, setTestsData] = useState([]);

    useEffect(() => {
        const fetchTests = async () => {
            try {
                const data = await getTests();
                if (Array.isArray(data.tests)) setTestsData(data.tests);
                else console.error("Fehler: 'tests' ist kein Array", data);
            } catch (err) {
                console.error(err);
            }
        };
        fetchTests();
    }, []);

    return (
        <Routes>
            <Route path='/' element={<ListView tests={testsData} />} />
            <Route path='/:articleID' element={<Article />} />
        </Routes>
    );
};

export default Tests;
